import React from 'react';
import { colors } from '../styles/colors';

interface ConfirmCreditsModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  recruiterName: string;
  changes: {
    opportunities: number;
    highlights: number;
    confidentials: number;
  };
  currentCredits: {
    opportunities: number;
    highlights: number;
    confidentials: number;
  };
  message: string;
}

const ConfirmCreditsModal: React.FC<ConfirmCreditsModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  recruiterName,
  changes,
  currentCredits,
  message
}) => {
  if (!isOpen) return null;

  const hasChanges = changes.opportunities !== 0 || changes.highlights !== 0 || changes.confidentials !== 0;

  const getNewValue = (current: number, change: number) => current + change;

  const formatChange = (value: number) => {
    if (value > 0) return `+${value}`;
    return value.toString();
  };

  const getChangeColor = (value: number) => {
    if (value > 0) return colors.success;
    if (value < 0) return colors.danger;
    return colors.textLight;
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>Confirmar Alteração de Créditos</h3>
        </div>
        
        <div style={styles.content}>
          <div style={styles.recruiterInfo}>
            <strong>Recrutador:</strong> {recruiterName}
          </div>
          
          <div style={styles.messageInfo}>
            <strong>Motivo:</strong> {message}
          </div>

          {hasChanges && (
            <div style={styles.changesSection}>
              <h4 style={styles.sectionTitle}>Alterações:</h4>
              
              <div style={styles.changesGrid}>
                {changes.opportunities !== 0 && (
                  <div style={styles.changeItem}>
                    <div style={styles.changeHeader}>
                      <span style={styles.creditType}>Vagas</span>
                      <span style={{ ...styles.changeValue, color: getChangeColor(changes.opportunities) }}>
                        {formatChange(changes.opportunities)}
                      </span>
                    </div>
                    <div style={styles.calculation}>
                      {currentCredits.opportunities} → {getNewValue(currentCredits.opportunities, changes.opportunities)}
                    </div>
                  </div>
                )}
                
                {changes.highlights !== 0 && (
                  <div style={styles.changeItem}>
                    <div style={styles.changeHeader}>
                      <span style={styles.creditType}>Destaques</span>
                      <span style={{ ...styles.changeValue, color: getChangeColor(changes.highlights) }}>
                        {formatChange(changes.highlights)}
                      </span>
                    </div>
                    <div style={styles.calculation}>
                      {currentCredits.highlights} → {getNewValue(currentCredits.highlights, changes.highlights)}
                    </div>
                  </div>
                )}
                
                {changes.confidentials !== 0 && (
                  <div style={styles.changeItem}>
                    <div style={styles.changeHeader}>
                      <span style={styles.creditType}>Confidenciais</span>
                      <span style={{ ...styles.changeValue, color: getChangeColor(changes.confidentials) }}>
                        {formatChange(changes.confidentials)}
                      </span>
                    </div>
                    <div style={styles.calculation}>
                      {currentCredits.confidentials} → {getNewValue(currentCredits.confidentials, changes.confidentials)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div style={styles.actions}>
          <button onClick={onCancel} style={styles.cancelBtn}>
            Cancelar
          </button>
          <button onClick={onConfirm} style={styles.confirmBtn}>
            Confirmar Alteração
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  },
  header: {
    padding: '1.5rem 1.5rem 1rem 1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '1.3rem',
  },
  content: {
    padding: '1.5rem',
  },
  recruiterInfo: {
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    fontSize: '0.95rem',
  },
  messageInfo: {
    marginBottom: '1.5rem',
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    fontSize: '0.95rem',
    borderLeft: `4px solid ${colors.primary}`,
  },
  changesSection: {
    marginTop: '1.5rem',
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    color: colors.text,
    fontSize: '1.1rem',
  },
  changesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  changeItem: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
  },
  changeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  creditType: {
    fontWeight: 'bold',
    color: colors.text,
  },
  changeValue: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  calculation: {
    fontSize: '0.9rem',
    color: colors.textLight,
    fontFamily: 'monospace',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    padding: '1rem 1.5rem 1.5rem 1.5rem',
    borderTop: `1px solid ${colors.border}`,
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.textLight,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
  },
  confirmBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
  },
};

export default ConfirmCreditsModal;